/**
 * Altura útil no mobile — usada em calc() (agenda, modais). O shell do app
 * preenche a tela via CSS (`.app-viewport-shell { position: fixed; inset: 0 }`),
 * não via esta variável como teto fixo.
 */
const VIEWPORT_VAR = '--ui-viewport-height';
const VIEWPORT_FALLBACK_VAR = '--ui-viewport-height-fallback';
const VIEWPORT_OFFSET_TOP_VAR = '--ui-viewport-offset-top';

function applyViewportHeightPx(px: number): void {
  const h = `${Math.max(0, Math.round(px))}px`;
  document.documentElement.style.setProperty(VIEWPORT_VAR, h);
  document.documentElement.style.setProperty(VIEWPORT_FALLBACK_VAR, h);
}

function readViewportHeightPx(): number {
  const inner = window.innerHeight;
  const client = document.documentElement.clientHeight || 0;
  const vv = window.visualViewport;
  const vvSpan = vv && vv.height > 0 ? Math.round(vv.height + (vv.offsetTop ?? 0)) : 0;
  const avail = typeof window.screen?.availHeight === 'number' ? window.screen.availHeight : 0;
  return Math.max(inner, client, vvSpan, avail);
}

function shouldSyncViewportHeight(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(min-width: 960px)').matches) return false;
  return true;
}

export function startViewportHeightSync(): () => void {
  if (typeof window === 'undefined') return () => {};

  const sync = () => {
    if (!shouldSyncViewportHeight()) {
      document.documentElement.style.removeProperty(VIEWPORT_VAR);
      document.documentElement.style.removeProperty(VIEWPORT_FALLBACK_VAR);
      document.documentElement.style.setProperty(VIEWPORT_OFFSET_TOP_VAR, '0px');
      return;
    }

    applyViewportHeightPx(readViewportHeightPx());

    const offsetTop = window.visualViewport?.offsetTop ?? 0;
    document.documentElement.style.setProperty(
      VIEWPORT_OFFSET_TOP_VAR,
      `${Math.max(0, Math.round(offsetTop))}px`,
    );
  };

  sync();

  window.addEventListener('resize', sync, { passive: true });
  window.addEventListener('orientationchange', sync, { passive: true });
  window.visualViewport?.addEventListener('resize', sync, { passive: true });
  window.visualViewport?.addEventListener('scroll', sync, { passive: true });
  document.addEventListener('visibilitychange', sync, { passive: true });

  return () => {
    window.removeEventListener('resize', sync);
    window.removeEventListener('orientationchange', sync);
    window.visualViewport?.removeEventListener('resize', sync);
    window.visualViewport?.removeEventListener('scroll', sync);
    document.removeEventListener('visibilitychange', sync);
  };
}
